import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import FormTextField from './FormTextField';
import FormSelectField from './FormSelectField';
import FormCheckbox from './FormCheckbox';
import { generateFiscalDataSchema } from './validationSchema';
import { useTemplateConfig } from 'legacyfrontend/hooks/useTemplateConfig';

const defaultInitialValues = {
  documentType: '',
  cuitCuil: '',
};

const FISCAL_CONFIGS = {
  1: {
    documentOptions: ['CUIL', 'CUIT'],
    documentName: 'CUIT/CUIL',
    format: (value) => {
      if (!value) return value;
      const numbers = value.replace(/\D/g, '');
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 10) {
        return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
      }
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 10)}-${numbers.slice(10, 11)}`;
    },
    fieldLabel: 'Número',
    fieldHint: 'Formato: XX-XXXXXXXX-X',
    maxLength: 13,
  },
  2: {
    documentOptions: ['RUC'],
    documentName: 'RUC',
    format: (value) => {
      if (!value) return value;
      const numbers = value.replace(/\D/g, '');
      if (numbers.length <= 8) {
        return numbers;
      }
      return `${numbers.slice(0, 8)}-${numbers.slice(8, 11)}`;
    },
    fieldLabel: 'Número RUC',
    fieldHint: 'Formato: XXXXXXXX-XXX',
    maxLength: 11,
  },
  3: {
    documentOptions: ['CNPJ'],
    documentName: 'CNPJ',
    format: (value) => {
      if (!value) return value;
      const numbers = value.replace(/\D/g, '');
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 5) {
        return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
      }
      if (numbers.length <= 8) {
        return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
      }
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
    },
    fieldLabel: 'Número CNPJ',
    fieldHint: 'Formato: XX.XXX.XXX/XXXX-XX',
    maxLength: 18,
  },
  4: {
    documentOptions: ['RFC'],
    documentName: 'RFC',
    format: (value) => value?.toUpperCase() || value,
    fieldLabel: 'Número RFC',
    fieldHint: 'RFC válido',
    maxLength: 13,
  },
};

const FiscalDataSection = memo(
  ({
    index,
    background,
    useCommonFiscalData,
    commonFiscalDataPassengerIndex,
    commonFiscalData,
    onToggleCommonData,
    handleCommonFiscalDataChange,
    handleIndividualFiscalDataChange,
    updateFiscalData,
    initialValues,
    totalPassengers,
    isInternational,
    countryCode,
  }) => {
    const { paisId } = useTemplateConfig();
    if (!isInternational) {
      return null;
    }

    const fiscalConfig = useMemo(() => {
      return FISCAL_CONFIGS[paisId] || FISCAL_CONFIGS[1];
    }, [paisId]);

    const validationSchema = useMemo(() => {
      return generateFiscalDataSchema(fiscalConfig.documentOptions, fiscalConfig.documentName);
    }, [fiscalConfig]);

    const safeInitialValues = initialValues || defaultInitialValues;

    const formik = useFormik({
      initialValues: useCommonFiscalData ? commonFiscalData : safeInitialValues,
      validationSchema: validationSchema,
      validateOnMount: false,
      validateOnChange: true,
      validateOnBlur: true,
    });

    const singleDocumentType = useMemo(() => {
      return fiscalConfig?.documentOptions?.length === 1 ? fiscalConfig.documentOptions[0] : null;
    }, [fiscalConfig]);

    const formatFiscalNumber = useCallback((value) => {
      return fiscalConfig.format(value);
    }, [fiscalConfig]);

    useEffect(() => {
      if (!singleDocumentType) return;

      if (useCommonFiscalData && index !== commonFiscalDataPassengerIndex) return;

      const current = formik.values.documentType;
      const isValidCurrent = fiscalConfig.documentOptions.includes(current);
      if (current === singleDocumentType && isValidCurrent) return;

      if (current && isValidCurrent) return;

      formik.setFieldValue('documentType', singleDocumentType, true);

      if (useCommonFiscalData) {
        handleCommonFiscalDataChange('documentType', singleDocumentType);
      } else {
        handleIndividualFiscalDataChange(index, 'documentType', singleDocumentType);
      }
    }, [
      singleDocumentType,
      fiscalConfig,
      useCommonFiscalData,
      index,
      commonFiscalDataPassengerIndex,
      formik.values.documentType,
      formik.setFieldValue,
      handleCommonFiscalDataChange,
      handleIndividualFiscalDataChange,
    ]);

    useEffect(() => {
      if (useCommonFiscalData) {
        formik.setValues({
          documentType: commonFiscalData.documentType,
          cuitCuil: commonFiscalData.cuitCuil,
        });

        if (index === commonFiscalDataPassengerIndex) {
          formik.validateForm().then((errors) => {
            const hasErrors = Object.keys(errors).length > 0;
            if (hasErrors) {
              handleCommonFiscalDataChange('validationStatus', hasErrors);
            }
          });
        }
      } else if (initialValues) {
        formik.setValues({
          documentType: initialValues.documentType || '',
          cuitCuil: initialValues.cuitCuil || '',
        });

        if (formik.touched.documentType || formik.touched.cuitCuil) {
          formik.setTouched({
            documentType: false,
            cuitCuil: false,
          });
        }
      }
    }, [
      useCommonFiscalData,
      commonFiscalData,
      index,
      commonFiscalDataPassengerIndex,
      initialValues,
    ]);

    const handleChange = useCallback(
      (e) => {
        const { name, value } = e.target;

        if (name === 'cuitCuil' || name === 'documentNumber') {
          const formattedValue = formatFiscalNumber(value);
          formik.setFieldValue(name, formattedValue, true);

          if (useCommonFiscalData) {
            handleCommonFiscalDataChange(name, formattedValue);
          } else {
            handleIndividualFiscalDataChange(index, name, formattedValue);
          }
        } else if (name === 'documentType') {
          const currentDocumentNumber = formik.values.cuitCuil || formik.values.documentNumber || '';
          formik.setFieldValue(name, value, true);

          if (useCommonFiscalData) {
            handleCommonFiscalDataChange(name, value);
            handleCommonFiscalDataChange('cuitCuil', currentDocumentNumber);
          } else {
            handleIndividualFiscalDataChange(index, name, value);
            handleIndividualFiscalDataChange(index, 'cuitCuil', currentDocumentNumber);
          }
        } else {
          formik.setFieldValue(name, value, true);

          if (useCommonFiscalData) {
            handleCommonFiscalDataChange(name, value);
          } else {
            handleIndividualFiscalDataChange(index, name, value);
          }
        }
      },
      [
        useCommonFiscalData,
        handleCommonFiscalDataChange,
        handleIndividualFiscalDataChange,
        index,
        formik,
        formatFiscalNumber,
      ]
    );

    const handleBlur = useCallback(
      (e) => {
        const { name } = e.target;
        formik.handleBlur(e);
        formik.setFieldTouched(name, true, true);

        formik.validateField(name).then(() => {
          const { errors } = formik;
          const hasErrors = Object.keys(errors).length > 0;

          if (useCommonFiscalData && index === commonFiscalDataPassengerIndex) {
            handleCommonFiscalDataChange('validationStatus', hasErrors);
          } else if (!useCommonFiscalData) {
            const updatedData = {
              documentType: formik.values.documentType,
              documentNumber: formik.values.documentNumber,
              cuitCuil: formik.values.cuitCuil,
            };
            updateFiscalData(index, updatedData, false, hasErrors);
          }
        });
      },
      [
        formik,
        index,
        useCommonFiscalData,
        commonFiscalDataPassengerIndex,
        handleCommonFiscalDataChange,
        updateFiscalData,
      ]
    );

    const handleToggleCommonData = useCallback(
      (e) => {
        const isChecked = e.target.checked;
        const currentValues = formik.values;

        formik.validateForm().then((errors) => {
          const hasErrors = Object.keys(errors).length > 0;
          onToggleCommonData(index, isChecked ? currentValues : null, hasErrors);
        });
      },
      [index, formik, onToggleCommonData]
    );

    const showErrors = useCommonFiscalData
      ? index === commonFiscalDataPassengerIndex // Solo mostrar errores en el pasajero principal cuando se usan datos comunes
      : true; // Mostrar errores individuales cuando no se usan datos comunes

    return (
      <>
        <Grid item xs={12} data-testid='fiscal-data-container'>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 1,
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: '1.1rem' }} variant='h6'>
              Datos fiscales <span style={{ color: 'red' }}>*</span>
            </Typography>
            {totalPassengers > 1 && (
              <>
                {useCommonFiscalData && commonFiscalDataPassengerIndex !== index ? (
                  <Typography
                    sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}
                    variant='body1'
                  >
                    Se está usando los datos del Pasajero {commonFiscalDataPassengerIndex + 1}
                  </Typography>
                ) : (
                  <FormCheckbox
                    background={background}
                    checked={useCommonFiscalData}
                    label={
                      useCommonFiscalData
                        ? `Usar datos del Pasajero ${commonFiscalDataPassengerIndex + 1}`
                        : 'Usar datos en todos los pasajeros'
                    }
                    onChange={handleToggleCommonData}
                  />
                )}
              </>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormSelectField
                disabled={
                  (useCommonFiscalData && index !== commonFiscalDataPassengerIndex) ||
                  Boolean(singleDocumentType)
                }
                error={
                  showErrors && formik.touched.documentType && Boolean(formik.errors.documentType)
                }
                formik={formik}
                helperText={
                  showErrors && formik.touched.documentType ? formik.errors.documentType : ''
                }
                label='Tipo de documento'
                name='documentType'
                onBlur={handleBlur}
                onChange={handleChange}
              >
                {fiscalConfig.documentOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </FormSelectField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormTextField
                disabled={
                  (useCommonFiscalData && index !== commonFiscalDataPassengerIndex) ||
                  !formik.values.documentType
                }
                error={showErrors && formik.touched.cuitCuil && Boolean(formik.errors.cuitCuil)}
                formik={formik}
                helperText={
                  (showErrors && formik.touched.cuitCuil && formik.errors.cuitCuil) ||
                  fiscalConfig.fieldHint
                }
                inputProps={{ maxLength: fiscalConfig.maxLength }}
                inputType='fiscal'
                label={fiscalConfig.fieldLabel}
                name='cuitCuil'
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }
);

FiscalDataSection.displayName = 'FiscalDataSection';

export default FiscalDataSection;
